import React, { useRef, useState } from 'react';
import axios from 'axios';
import { FileUp, FileText, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 10;
const MAX_MB = 10;

export const ReportUpload = ({ files, onChange, onBusy }) => {
  const input = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const pick = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = '';
    setError('');
    let list = files;
    for (const f of chosen) {
      if (list.length >= MAX_FILES) { setError(`You can upload up to ${MAX_FILES} files.`); break; }
      if (f.size > MAX_MB * 1024 * 1024) { setError(`${f.name} is larger than ${MAX_MB} MB.`); continue; }
      onBusy(true);
      setProgress({ name: f.name, pct: 0 });
      try {
        const body = new FormData();
        body.append('file', f);
        const { data } = await axios.post(`${API}/reports`, body, { onUploadProgress: (p) => setProgress({ name: f.name, pct: Math.round((p.loaded / (p.total || f.size)) * 100) }) });
        list = [...list, data];
        onChange(list);
      } catch (err) {
        setError(err.response?.data?.detail || `Could not upload ${f.name}. Please try again.`);
      }
    }
    setProgress(null);
    onBusy(false);
  };

  return (
    <div data-testid="report-upload" className="rounded-2xl border border-dashed border-teal-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-teal-900">Medical reports</p>
          <p className="text-xs text-slate-500">Blood tests, scans, letters. PDF or photos, up to {MAX_MB} MB each.</p>
        </div>
        <button type="button" data-testid="report-upload-button" onClick={() => input.current?.click()} disabled={!!progress}
          className="btn-outline inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-white disabled:opacity-60">
          <FileUp size={16} /> Add files
        </button>
        <input ref={input} data-testid="report-upload-input" type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={pick} />
      </div>
      {progress && (
        <div data-testid="report-upload-progress" className="mt-3 text-xs text-slate-600">
          <div className="flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Uploading {progress.name}… {progress.pct}%</div>
          <div className="h-1.5 rounded-full bg-teal-50 mt-1.5 overflow-hidden"><div className="h-full bg-teal-500 transition-[width]" style={{ width: `${progress.pct}%` }} /></div>
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {files.map((f) => (
            <li key={f.id} data-testid={`report-file-${f.id}`} className="flex items-center justify-between gap-2 text-sm bg-teal-50/60 rounded-lg px-3 py-2">
              <span className="flex items-center gap-2 truncate text-slate-700"><FileText size={14} className="text-teal-600 shrink-0" /> <span className="truncate">{f.filename}</span></span>
              <button type="button" data-testid={`report-remove-${f.id}`} aria-label="Remove file" onClick={() => onChange(files.filter((x) => x.id !== f.id))} className="text-slate-400 hover:text-red-500"><X size={15} /></button>
            </li>
          ))}
        </ul>
      )}
      {error && <p data-testid="report-upload-error" className="text-xs mt-2" style={{ color: 'var(--coral-dark)' }}>{error}</p>}
    </div>
  );
};
